import {ReactNode} from 'react'
import {toast, ToastOptions, ToastPosition, TypeOptions} from 'react-toastify'
import {nanoid} from 'nanoid'

import {ToastCheckIcon, ToastCloseIcon, TriangleWarningIcon} from 'components'
import {colorCodes} from 'definitions/constant'

export const toastStyles = {
  success: {
    style: {
      background: '#081d15',
      border: `1px solid ${colorCodes.green100}`,
      borderRadius: 0,
      color: colorCodes.green100,
    },
    icon: (
      <div style={{marginLeft: '-2px'}}>
        <ToastCheckIcon />
      </div>
    ),
    closeButton: false,
  },
  error: {
    style: {
      background: '#3a1d1a',
      border: `1px solid ${colorCodes.red100}`,
      borderRadius: 0,
      color: colorCodes.red100,
    },
    icon: (
      <div style={{marginLeft: '-2px'}}>
        <ToastCloseIcon />
      </div>
    ),
    closeButton: false,
  },
  info: {
    style: {
      background: `linear-gradient(0deg, rgba(0, 190, 255, 0.3), rgba(0, 190, 255, 0.3)), ${colorCodes.dopeBlack}`,
      border: `1px solid ${colorCodes.cyanBright}`,
      borderRadius: 0,
    },
    icon: (
      <div style={{marginLeft: '-2px'}}>
        <ToastCloseIcon strokeColor="text-dope-cyan-bright" />
      </div>
    ),
    closeButton: false,
  },
  warning: {
    style: {
      background: `linear-gradient(0deg, rgba(247, 200, 57, 0.2), rgba(247, 200, 57, 0.2)), ${colorCodes.dopeBlack}`,
      border: `1px solid ${colorCodes.yellow100}`,
      borderRadius: 0,
      color: colorCodes.yellow100,
    },
    icon: (
      <div style={{marginLeft: '-2px'}}>
        <TriangleWarningIcon strokeColor="text-yellow-100" />
      </div>
    ),
    closeButton: false,
  },
}

interface Props {
  message: string | ReactNode | unknown
  type: TypeOptions
  position?: ToastPosition
  customId?: string
  icon?: ReactNode
  style?: React.CSSProperties
  autoClose?: number
}

const Toast = ({
  message,
  type,
  position = 'bottom-center',
  customId,
  icon,
  style = {},
  autoClose = 5000,
}: Props) => {
  if (!message) {
    return null
  }

  let props: ToastOptions = {}
  const toastId = customId ?? nanoid()
  const getToastProps = (newProps: ToastOptions = {}) => {
    return {
      toastId,
      position,
      autoClose,
      ...newProps,
      icon: icon || newProps?.icon,
      style: {
        ...newProps.style,
        ...style,
      },
      role: 'alert',
    }
  }

  // dismiss currently open toast
  toast.dismiss()

  switch (type) {
    case 'success':
      props = getToastProps(toastStyles.success)
      break
    case 'error':
      props = getToastProps(toastStyles.error)
      break
    case 'warning':
      props = getToastProps(toastStyles.warning)
      break
    case 'info':
      props = getToastProps(toastStyles.info)
      break
    default:
      props = getToastProps()
      break
  }

  return toast(message as ReactNode, props)
}

export default Toast
